import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Phone, CheckCircle2, AlertCircle, MapPin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

interface PhoneStepProps {
  onNext: (data: { phone: string }) => void;
}

export const PhoneStep = ({ onNext }: PhoneStepProps) => {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [detectingCountry, setDetectingCountry] = useState(true);
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const { toast } = useToast();

  // Auto-detect country and phone code on mount
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-country-from-ip');
        
        if (error) {
          logger.error('Failed to detect country from IP', error);
          setDetectingCountry(false);
          return;
        }

        if (data?.phoneCode) {
          setPhone(data.phoneCode);
          setDetectedCountry(data.country);
          logger.info('Country auto-detected', { 
            country: data.country, 
            phoneCode: data.phoneCode,
            source: data.source 
          });
        }
      } catch (error) {
        logger.error('Error detecting country', error);
      } finally {
        setDetectingCountry(false);
      }
    };

    detectCountry();
  }, []);

  // Validate phone format (international format)
  const isPhoneValid = /^\+?[1-9]\d{1,14}$/.test(phone);
  const canSubmit = phone && isPhoneValid;

  const formatPhoneDisplay = (value: string) => {
    // Remove non-digits except + at start
    let cleaned = value.replace(/[^\d+]/g, '');
    
    // Ensure + is only at start
    if (cleaned.indexOf('+') > 0) {
      cleaned = '+' + cleaned.replace(/\+/g, '');
    }
    
    return cleaned;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneDisplay(e.target.value);
    setPhone(formatted);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || loading) return;

    setLoading(true);
    
    // Just validate format and proceed
    // Actual SMS will be sent in OTP step
    if (isPhoneValid) {
      onNext({ phone });
    } else {
      toast({
        variant: "destructive",
        title: "Número inválido",
        description: "Use o formato internacional (ex: +5511999999999)",
      });
    }
    
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-2"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6, bounce: 0.4 }}
          className="w-16 h-16 bg-gradient-to-br from-primary to-primary/60 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/20"
        >
          <Phone className="w-8 h-8 text-white" />
        </motion.div>
        <h1 className="text-xl font-bold">Número de Telefone</h1>
        <p className="text-muted-foreground">
          Enviaremos um código SMS para verificação
        </p>
        
        {/* Country detection indicator */}
        <AnimatePresence>
          {detectingCountry && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center justify-center gap-2 text-xs text-muted-foreground"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                <MapPin className="w-3 h-3" />
              </motion.div>
              Detectando seu país...
            </motion.div>
          )}
          {!detectingCountry && detectedCountry && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center justify-center gap-2 text-xs text-success"
            >
              <MapPin className="w-3 h-3" />
              País detectado: {detectedCountry}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-2"
        >
          <Label htmlFor="phone">Telefone (com código do país)</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground transition-colors" />
            <Input
              id="phone"
              type="tel"
              placeholder="+55 11 99999-9999"
              value={phone}
              onChange={handlePhoneChange}
              onBlur={() => setPhoneTouched(true)}
              disabled={loading || detectingCountry}
              className="pl-10 pr-10 transition-all duration-200"
              autoComplete="tel"
              autoFocus={!detectingCountry}
            />
            <AnimatePresence>
              {phoneTouched && isPhoneValid && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <CheckCircle2 className="w-5 h-5 text-success" />
                </motion.div>
              )}
              {phoneTouched && !isPhoneValid && phone && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <AlertCircle className="w-5 h-5 text-destructive" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          <AnimatePresence>
            {phoneTouched && !isPhoneValid && phone && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-xs text-destructive flex items-center gap-1"
              >
                <AlertCircle className="w-3 h-3" />
                Use formato internacional: +55 11 99999-9999
              </motion.p>
            )}
          </AnimatePresence>

          {!detectingCountry && (
            <p className="text-xs text-muted-foreground">
              Exemplo: +55 (Brasil), +1 (EUA), +351 (Portugal)
            </p>
          )}
          {detectingCountry && (
            <p className="text-xs text-muted-foreground">
              Aguarde enquanto detectamos seu país...
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Button
            type="submit"
            disabled={!canSubmit || loading || detectingCountry}
            className="w-full h-12 text-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            size="lg"
          >
            {detectingCountry ? (
              <span className="flex items-center gap-2">
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                />
                Detectando país...
              </span>
            ) : loading ? (
              <span className="flex items-center gap-2">
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                />
                Verificando...
              </span>
            ) : (
              "Continuar"
            )}
          </Button>
        </motion.div>
      </form>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center text-xs text-muted-foreground bg-muted/30 rounded-lg p-3"
      >
        {detectedCountry ? (
          <p>
            🌍 <strong>País detectado:</strong> {detectedCountry}. O código já foi preenchido para você!
          </p>
        ) : (
          <p>
            💡 <strong>Dica:</strong> Certifique-se de incluir o código do país (ex: +55 para Brasil)
          </p>
        )}
      </motion.div>
    </div>
  );
};